// conectagran/frontend/src/pages/AdManager/GamUrlTable.tsx
import { useState } from 'react'
import { Search, Loader2 } from 'lucide-react'
import type { GamUrlReport } from '@/api/hooks/useGam'

interface Props {
  data: GamUrlReport | undefined
  isLoading: boolean
}

export function GamUrlTable({ data, isLoading }: Props) {
  const [search, setSearch] = useState('')

  if (isLoading || !data) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-[#64748b]">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Carregando...</span>
      </div>
    )
  }

  if (data.status === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-[#64748b]">
        <Loader2 className="w-5 h-5 animate-spin text-[#c9a84c]" />
        <span className="text-sm">Gerando relatório no GAM… pode levar até 2 minutos.</span>
      </div>
    )
  }

  if (data.status === 'error') {
    return (
      <div className="py-16 text-center text-[#ef4444] text-sm">
        Erro: {data.message ?? 'Falha ao gerar relatório.'}
      </div>
    )
  }

  const rows = (data.rows ?? []).filter(r =>
    !search || r.url.toLowerCase().includes(search.toLowerCase())
  )

  const totalImpressions = rows.reduce((s, r) => s + r.impressions, 0)
  const totalRevenue     = rows.reduce((s, r) => s + r.revenue_usd, 0)
  const totalRevenueBrl  = rows.reduce((s, r) => s + (r.revenue_brl ?? 0), 0)
  const totalClicks      = rows.reduce((s, r) => s + r.clicks, 0)
  const avgEcpm          = totalImpressions > 0 ? (totalRevenue / totalImpressions) * 1000 : 0
  const avgCtr           = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0

  return (
    <div>
      {/* Search */}
      <div className="px-5 py-3 border-b border-[#1e2235] bg-[#0b0d14]">
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#475569]" />
          <input
            type="text"
            placeholder="Buscar URL…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-[#1a1d2a] border border-[#252836] text-[#e2e8f0] text-xs rounded-md pl-8 pr-3 py-1.5 placeholder:text-[#475569] focus:outline-none focus:border-[#c9a84c]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[#1e2235] bg-[#0b0d14] text-[#64748b] uppercase tracking-widest text-[11px]">
              <th className="text-left px-5 py-2.5 font-semibold">URL</th>
              <th className="text-right px-4 py-2.5 font-semibold">Impressões</th>
              <th className="text-right px-4 py-2.5 font-semibold">Receita $</th>
              <th className="text-right px-4 py-2.5 font-semibold">Receita R$</th>
              <th className="text-right px-4 py-2.5 font-semibold">eCPM</th>
              <th className="text-right px-5 py-2.5 font-semibold">CTR</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-[#13151f] hover:bg-[#0f1119] transition-colors">
                <td className="px-5 py-3 text-[#94a3b8] font-mono text-[11px] max-w-[420px] truncate">
                  <a href={r.url} target="_blank" rel="noreferrer" className="hover:text-[#c9a84c] transition-colors" title={r.url}>
                    {r.url.replace(/^https?:\/\//, '')}
                  </a>
                </td>
                <td className="text-right px-4 py-3 text-sm tabular-nums text-[#94a3b8]">
                  {r.impressions >= 1000 ? `${(r.impressions / 1000).toFixed(1)}K` : r.impressions}
                </td>
                <td className="text-right px-4 py-3 text-sm tabular-nums text-[#94a3b8]">
                  ${r.revenue_usd.toFixed(2)}
                </td>
                <td className="text-right px-4 py-3 text-sm tabular-nums text-[#c9a84c] font-semibold">
                  R$ {(r.revenue_brl ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="text-right px-4 py-3 text-sm tabular-nums text-[#cbd5e1]">${r.ecpm.toFixed(2)}</td>
                <td className="text-right px-5 py-3 text-sm tabular-nums text-[#94a3b8]">{r.ctr.toFixed(2)}%</td>
              </tr>
            ))}

            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-10 text-[#475569]">
                  {search ? 'Nenhuma URL encontrada.' : 'Sem dados para o período.'}
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[#1e2235] bg-[#0b0d14] text-[#f8fafc] font-semibold">
                <td className="px-5 py-2.5 text-sm">{rows.length} URLs{search ? ` (filtrado)` : ''}</td>
                <td className="text-right px-4 py-2.5 text-sm tabular-nums">
                  {totalImpressions >= 1000 ? `${(totalImpressions / 1000).toFixed(1)}K` : totalImpressions}
                </td>
                <td className="text-right px-4 py-2.5 text-sm tabular-nums">${totalRevenue.toFixed(2)}</td>
                <td className="text-right px-4 py-2.5 text-sm tabular-nums text-[#c9a84c]">R$ {totalRevenueBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td className="text-right px-4 py-2.5 text-sm tabular-nums text-[#c9a84c]">${avgEcpm.toFixed(2)}</td>
                <td className="text-right px-5 py-2.5 text-sm tabular-nums">{avgCtr.toFixed(2)}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
